import React from 'react';
import { Dropdown } from '@doveit/bricks';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { Applicant } from '../../../providers/api/dtos';
import BaseUpdateOptInsAction, { GenericOptIn } from '../../../opt-ins/containers/base-update-optins-action/BaseUpdateOptInsAction';
import BaseOptInsWidget from '../../../opt-ins/containers/base-optins-widget/BaseOptInsWidget';
import { useApplicant } from '../../hooks/use-applicant/useApplicant';
import { updateApplicant } from '../../../providers/api/applicant/applicantProvider';

export interface ApplicantOptInsWidgetProps {
  applicantId: NonNullable<Applicant['id']>;
  canAdd?: boolean;
  canDelete?: boolean;
  canSeeDetails?: boolean;
  onLoadError?: VoidFunction;
}

const ApplicantOptInsWidget: React.FC<ApplicantOptInsWidgetProps> = ({
  applicantId,
  canAdd = false,
  canDelete = false,
  canSeeDetails = false,
  onLoadError,
}) => {
  const isMobile = useIsDevice('mobile');
  const {
    data: applicant,
    error: applicantError,
    isLoading: isApplicantLoading,
    mutate: mutateApplicant,
  } = useApplicant(applicantId, { onError: onLoadError });

  const handleAdd = React.useCallback(async (optIn: GenericOptIn) => {
    const newApplicant: Applicant = { ...applicant!, optIns: [...applicant!.optIns, optIn] };
    await updateApplicant(applicantId, newApplicant);
    await mutateApplicant();
  }, [applicant, applicantId, mutateApplicant]);

  const handleDelete = React.useCallback(async (optIn: GenericOptIn) => {
    const newApplicant: Applicant = { ...applicant!, optIns: [...applicant!.optIns.filter((o) => o.type !== optIn.type)] };
    await updateApplicant(applicantId, newApplicant);
    await mutateApplicant();
  }, [applicant, applicantId, mutateApplicant]);

  const actionComponent = applicant && canSeeDetails ? (
    <BaseUpdateOptInsAction
      optIns={applicant.optIns}
      onAdd={handleAdd}
      onDelete={handleDelete}
      canAdd={canAdd}
      canDelete={canDelete}
    />
  ) : undefined;

  const mobileActionComponent = applicant && canSeeDetails ? (
    <BaseUpdateOptInsAction
      optIns={applicant.optIns}
      onAdd={handleAdd}
      onDelete={handleDelete}
      canAdd={canAdd}
      canDelete={canDelete}
    >
      {({ update, label, isSaving }) => (
        <Dropdown.Option
          label={label}
          aria-label="Apri modale di modifica consensi"
          onClick={update}
          loading={isSaving}
        />
      )}
    </BaseUpdateOptInsAction>
  ) : undefined;

  return (
    <BaseOptInsWidget
      optIns={applicant?.optIns}
      isLoading={isApplicantLoading}
      error={!!applicantError}
      primaryAction={!isMobile ? actionComponent : undefined}
      secondaryAction={isMobile ? mobileActionComponent : undefined}
    />
  );
};

export default ApplicantOptInsWidget;
